import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Paperclip, Send, MessageCircle, Edit2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskDetail = ({ user }) => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedByUser, setAssignedByUser] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchTask();
    fetchComments();
  }, [taskId]);

  const fetchTask = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTask(response.data);
      
      // Fetch all users to map assigned_to and assigned_by
      const usersResponse = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get assigned users (array)
      if (response.data.assigned_to && Array.isArray(response.data.assigned_to) && response.data.assigned_to.length > 0) {
        const assignedUsersList = usersResponse.data.filter(u => response.data.assigned_to.includes(u.id));
        setAssignedUsers(assignedUsersList);
      }
      
      // Get assigned_by user
      if (response.data.assigned_by) {
        const assignedBy = usersResponse.data.find(u => u.id === response.data.assigned_by);
        setAssignedByUser(assignedBy);
      }
    } catch (error) {
      toast.error('Failed to fetch task');
      navigate('/dashboard/tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/${taskId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments');
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/tasks/${taskId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTask({ ...task, status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/tasks/${taskId}/comments`,
        {
          comment_text: newComment,
          parent_comment_id: replyingTo
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment('');
      setReplyingTo(null);
      fetchComments();
      toast.success(replyingTo ? 'Reply posted!' : 'Comment posted!');
    } catch (error) {
      toast.error('Failed to post comment');
    }
  };

  const downloadAttachment = (attachment) => {
    const link = document.createElement('a');
    link.href = `data:${attachment.content_type};base64,${attachment.data}`;
    link.download = attachment.filename;
    link.click();
  };

  const getParentComments = () => {
    return comments.filter(c => !c.parent_comment_id);
  };

  const getReplies = (parentId) => {
    return comments.filter(c => c.parent_comment_id === parentId);
  };

  // Check if current user can edit the full task (creator or admin)
  const canEditFullTask = () => {
    if (!task || !user) return false;
    return task.assigned_by === user.id || 
           user.role === 'superadmin' || 
           user.role === 'ceo' ||
           user.role === 'manager';
  };

  // Check if current user can change status (assignee or admin)
  const canChangeStatus = () => {
    if (!task || !user) return false;
    return (task.assigned_to && Array.isArray(task.assigned_to) && task.assigned_to.includes(user.id)) || 
           task.picked_by?.includes(user.id) ||
           task.assigned_by === user.id ||
           user.role === 'superadmin' || 
           user.role === 'ceo' ||
           user.role === 'manager';
  };

  if (loading) return <div>Loading...</div>;
  if (!task) return <div>Task not found</div>;

  const statusOptions = [
    { value: 'pending', label: 'Pending', color: '#F59E0B' },
    { value: 'in_progress', label: 'In Progress', color: '#3B82F6' },
    { value: 'completed', label: 'Completed', color: '#10B981' }
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#7077FE',
              display: 'flex',
              alignItems: 'center',
              fontSize: '16px'
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <h2>Task Details</h2>
        </div>
        {canEditFullTask() && (
          <button
            onClick={() => navigate(`/dashboard/tasks/edit/${taskId}`)}
            className="gradient-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Edit2 size={16} /> Edit Task
          </button>
        )}
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', display: 'grid', gap: '24px' }}>
        {/* Task Info Card */}
        <div className="card">
          <div style={{ marginBottom: '24px', borderBottom: '2px solid #f0f0f0', paddingBottom: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#1a1a1a', fontSize: '24px' }}>{task.title}</h3>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span className={`status-badge ${task.status}`}>{task.status?.replace('_', ' ')}</span>
              
              {/* Assigned To */}
              <span style={{ color: '#666', fontSize: '14px' }}>
                Assigned to: <strong>
                  {assignedUsers.length > 0 
                    ? assignedUsers.map(u => u.full_name).join(', ')
                    : 'Unassigned (Common Task)'}
                </strong>
              </span>
              
              {/* Assigned By */}
              {assignedByUser && (
                <span style={{ 
                  color: '#666', 
                  fontSize: '14px',
                  padding: '4px 8px',
                  background: '#FFF4E6',
                  borderRadius: '4px'
                }}>
                  Assigned by: <strong>{assignedByUser.full_name}</strong>
                </span>
              )}
              
              {/* Priority */}
              {task.priority && (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: task.priority >= 8 ? '#FEE2E2' : task.priority >= 5 ? '#FEF3C7' : '#D1FAE5',
                  color: task.priority >= 8 ? '#DC2626' : task.priority >= 5 ? '#D97706' : '#059669'
                }}>
                  Priority {task.priority}/10
                </span>
              )}
            </div>
          </div>

          {/* Status Change Section - Available to assignees */}
          {canChangeStatus() && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={16} style={{ color: '#059669' }} />
                Update Status
              </h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {statusOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                    disabled={updatingStatus || task.status === option.value}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: task.status === option.value ? `2px solid ${option.color}` : '1px solid #E5E7EB',
                      background: task.status === option.value ? `${option.color}15` : 'white',
                      color: task.status === option.value ? option.color : '#6B7280',
                      fontWeight: task.status === option.value ? '600' : '500',
                      fontSize: '13px',
                      cursor: task.status === option.value ? 'default' : 'pointer',
                      opacity: updatingStatus ? 0.6 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>Description</h4>
            <div 
              style={{ margin: 0, color: '#666', lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ __html: task.description }}
            />
          </div>

          {/* Attachments */}
          {task.attachments && task.attachments.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>
                <Paperclip size={18} style={{ display: 'inline', marginRight: '8px' }} />
                Attachments ({task.attachments.length})
              </h4>
              <div style={{ display: 'grid', gap: '16px' }}>
                {task.attachments.map((file, index) => {
                  const isImage = file.content_type?.startsWith('image/');
                  
                  if (isImage) {
                    // Display image preview
                    return (
                      <div key={index} style={{ display: 'grid', gap: '8px' }}>
                        <div style={{
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '2px solid #e0e0ff',
                          background: '#f9f9ff'
                        }}>
                          <img
                            src={`data:${file.content_type};base64,${file.data}`}
                            alt={file.filename}
                            style={{
                              width: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              display: 'block'
                            }}
                          />
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: '#f9f9ff',
                          borderRadius: '6px',
                          border: '1px solid #e0e0ff'
                        }}>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600', fontSize: '14px', color: '#333' }}>
                              {file.filename}
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button
                            onClick={() => downloadAttachment(file)}
                            style={{
                              padding: '8px 16px',
                              background: 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    // Display file attachment (PDF, DOCX, etc.)
                    return (
                      <div
                        key={index}
                        onClick={() => downloadAttachment(file)}
                        style={{
                          padding: '12px',
                          background: '#f9f9ff',
                          borderRadius: '8px',
                          border: '1px solid #e0e0ff',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#f9f9ff'}
                      >
                        <Paperclip size={20} style={{ color: '#7077FE' }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>{file.filename}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <span style={{ color: '#7077FE', fontSize: '14px' }}>Download</span>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="card">
          <h3 style={{ margin: '0 0 24px 0', color: '#1a1a1a', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageCircle size={24} className="text-purple-500" />
            Comments ({comments.length})
          </h3>

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} style={{ marginBottom: '24px' }}>
            {replyingTo && (
              <div style={{
                padding: '8px 12px',
                background: '#f0f0ff',
                borderRadius: '6px',
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '14px', color: '#7077FE' }}>
                  Replying to comment...
                </span>
                <button
                  type="button"
                  onClick={() => setReplyingTo(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={replyingTo ? "Write a reply..." : "Write a comment..."}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
              <button
                type="submit"
                disabled={!newComment.trim()}
                style={{
                  padding: '12px 24px',
                  background: newComment.trim() ? 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: newComment.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600'
                }}
              >
                <Send size={18} />
                {replyingTo ? 'Reply' : 'Comment'}
              </button>
            </div>
          </form>

          {/* Comments List */}
          <div style={{ display: 'grid', gap: '16px' }}>
            {getParentComments().length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '24px' }}>
                No comments yet. Be the first to comment!
              </p>
            ) : (
              getParentComments().map(comment => (
                <div key={comment.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px' }}>
                  {/* Parent Comment */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '16px',
                        flexShrink: 0
                      }}
                    >
                      {comment.user_name[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>{comment.user_name}</strong>
                        <span style={{ marginLeft: '12px', fontSize: '12px', color: '#999' }}>
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p style={{ margin: '0 0 8px 0', color: '#333', lineHeight: '1.6' }}>
                        {comment.comment_text}
                      </p>
                      <button
                        onClick={() => setReplyingTo(comment.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#7077FE',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        Reply
                      </button>
                    </div>
                  </div>

                  {/* Replies */}
                  {getReplies(comment.id).map(reply => (
                    <div
                      key={reply.id}
                      style={{
                        display: 'flex',
                        gap: '12px',
                        marginLeft: '52px',
                        marginTop: '16px',
                        paddingLeft: '16px',
                        borderLeft: '2px solid #e0e0ff'
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #CE8FFB 0%, #7077FE 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '14px',
                          flexShrink: 0
                        }}
                      >
                        {reply.user_name[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ fontSize: '14px' }}>{reply.user_name}</strong>
                          <span style={{ marginLeft: '12px', fontSize: '12px', color: '#999' }}>
                            {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p style={{ margin: 0, color: '#333', lineHeight: '1.6' }}>
                          {reply.comment_text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;
